import { useState } from 'react';
import { cn } from '../../lib/utils';
import {
  ChevronDown, ChevronUp, CheckCircle2, XCircle, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Severity Color System ──
const SEVERITY_COLORS = {
  critical: '#ef4444',    // Red — needs immediate attention / above target
  warning: '#f59e0b',     // Amber — borderline / needs monitoring
  stable: '#22c55e',      // Green — controlled / at target
  info: '#6b5ce7',        // Purple — informational / routine
};

const SEVERITY_LEGEND = [
  { color: SEVERITY_COLORS.critical, label: 'Needs attention' },
  { color: SEVERITY_COLORS.warning, label: 'Borderline' },
  { color: SEVERITY_COLORS.stable, label: 'Controlled' },
  { color: SEVERITY_COLORS.info, label: 'Routine' },
];

const statusToSeverity = (status) => {
  if (status === 'Needs attention' || status === 'Above target') return 'critical';
  if (status === 'Borderline' || status === 'Monitor') return 'warning';
  if (status === 'At target' || status === 'Controlled') return 'stable';
  return 'info';
};

const CONDITION_CONFIG = {
  diabetes: { label: 'Type 2 Diabetes', icd: 'E11.9' },
  hypertension: { label: 'Essential Hypertension', icd: 'I10' },
  hyperlipidemia: { label: 'Hyperlipidemia', icd: 'E78.5' },
};

const STATUS_CONFIG = {
  'Needs attention': { bg: '#fef2f2', color: '#b91c1c' },
  'At target': { bg: '#f0fdf4', color: '#16a34a' },
  'Controlled': { bg: '#f0fdf4', color: '#16a34a' },
  'Above target': { bg: '#fef2f2', color: '#b91c1c' },
  'Borderline': { bg: '#fffbeb', color: '#92400e' },
};

// ── Mock Disease Data ──
const DISEASE_DATA = {
  P001: {
    blocks: [
      {
        condition: 'diabetes',
        status: 'Needs attention',
        metrics: [
          { label: 'HbA1c', value: '7.2%', trend: 'up', target: '< 7.0%', date: '3/10/26' },
          { label: 'eGFR', value: '72 mL/min', trend: 'down', target: '> 60', date: '1/15/26' },
          { label: 'Fasting Glucose', value: '142 mg/dL', trend: 'up', target: '70-100', date: '1/15/26' },
          { label: 'Last eye exam', value: '14 mo ago', trend: 'up', target: 'Annual', date: '1/20/26' },
        ],
        actions: [
          { text: 'Titrate Metformin to 1500mg BID — A1c above target', soap: true },
          { text: 'Annual diabetic eye exam overdue by 2 months', soap: true },
          { text: 'Revisit GLP-1 discussion — patient declined (cost)', soap: false },
        ],
        lastReviewed: 'Jan 15, 2026',
      },
      {
        condition: 'hypertension',
        status: 'Needs attention',
        metrics: [
          { label: 'BP today', value: '138/88', trend: 'up', target: '< 130/80', date: 'today' },
          { label: 'BP 3-visit avg', value: '136/86', trend: 'up', target: '< 130/80', date: '' },
          { label: 'Current meds', value: 'Lisinopril 20mg', trend: null, target: '', date: '' },
        ],
        actions: [
          { text: 'BP exceeds target — consider adding amlodipine 5mg', soap: true },
          { text: 'Home BP monitoring setup recommended', soap: true },
          { text: 'MIPS: Controlling High BP — not at target', soap: false },
        ],
        lastReviewed: 'Jan 15, 2026',
      },
      {
        condition: 'hyperlipidemia',
        status: 'At target',
        metrics: [
          { label: 'LDL', value: '98 mg/dL', trend: 'down', target: '< 100', date: '1/15/26' },
          { label: 'Current meds', value: 'Atorvastatin 40mg QHS', trend: null, target: '', date: '' },
        ],
        actions: [
          { text: 'LDL at target — continue current regimen', soap: false },
          { text: 'Annual lipid panel due in 3 months', soap: false },
        ],
        lastReviewed: 'Jan 15, 2026',
      },
    ],
    preventive: {
      items: [
        { name: 'HbA1c', freq: 'Every 3 months', lastDone: 'Jan 2026', dueDate: 'Apr 2026', status: 'overdue', actionLabel: 'Order' },
        { name: 'Lipid panel', freq: 'Annual', lastDone: 'Jan 2026', dueDate: 'Jan 2027', status: 'current' },
        { name: 'Colonoscopy', freq: 'Every 10 years', lastDone: 'Mar 2016', dueDate: 'Mar 2026', status: 'overdue', actionLabel: 'Schedule' },
        { name: 'Annual wellness visit', freq: 'Annual', lastDone: 'Jan 2025', dueDate: 'Jan 2026', status: 'overdue', actionLabel: 'Schedule' },
        { name: 'Shingrix', freq: 'One-time (50+)', lastDone: 'Sep 2024', dueDate: '\u2014', status: 'current' },
        { name: 'PHQ-9', freq: 'Annual', lastDone: '\u2014', dueDate: 'Now', status: 'overdue', actionLabel: 'Update chart' },
        { name: 'Influenza', freq: 'Annual', lastDone: 'Oct 2025', dueDate: 'Oct 2026', status: 'current' },
      ],
    },
  },
};

const TREND_DISPLAY = {
  up: { symbol: '\u2191', color: '#ef4444' },
  down: { symbol: '\u2193', color: '#d97706' },
  stable: { symbol: '\u2014', color: '#9ca3af' },
};

const metricValueColor = (m) => {
  if (m.trend === 'up') return '#ef4444';
  if (m.trend === 'down') return '#d97706';
  if (m.value === 'Unknown') return '#ef4444';
  return '#111111';
};

// ── Condition Block ──
const ConditionBlock = ({ block, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const config = CONDITION_CONFIG[block.condition] || { label: block.condition, icd: '' };
  const statusConf = STATUS_CONFIG[block.status] || { bg: '#f5f5f5', color: '#555' };
  const severity = statusToSeverity(block.status);
  const dotColor = SEVERITY_COLORS[severity];

  return (
    <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, overflow: 'hidden', borderLeft: `3px solid ${dotColor}` }}
      data-testid={`disease-block-${block.condition}`}>
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2"
        style={{ padding: '10px 16px', cursor: 'pointer', background: 'transparent' }}
        data-testid={`condition-toggle-${block.condition}`}
      >
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 500, color: '#111111' }}>{config.label}</span>
        <span style={{ fontSize: 10, color: '#aaaaaa', background: '#f5f5f5', borderRadius: 3, padding: '1px 5px' }}>{config.icd}</span>
        <div className="flex-1" />
        <span style={{
          fontSize: 11, fontWeight: 500, borderRadius: 20, padding: '2px 8px',
          background: statusConf.bg, color: statusConf.color,
        }}>{block.status}</span>
        {expanded
          ? <ChevronUp style={{ width: 11, height: 11, color: '#cccccc' }} />
          : <ChevronDown style={{ width: 11, height: 11, color: '#cccccc' }} />
        }
      </button>

      {/* Expanded body */}
      {expanded && (
        <>
          <div style={{ borderTop: '0.5px solid #f5f5f5', padding: '12px 16px' }}>
            <div className="flex gap-4">
              {/* Left column — Metrics */}
              <div style={{ flex: '1 1 50%' }}>
                {block.metrics.slice(0, 4).map((m, i) => (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <p style={{ fontSize: 10, color: '#aaaaaa', marginBottom: 2 }}>{m.label}</p>
                    <div className="flex items-center gap-1.5">
                      <span style={{ fontSize: 18, fontWeight: 500, color: metricValueColor(m) }}>{m.value}</span>
                      {m.trend && TREND_DISPLAY[m.trend] && (
                        <span style={{ fontSize: 11, color: TREND_DISPLAY[m.trend].color, fontWeight: 600 }}>
                          {TREND_DISPLAY[m.trend].symbol}
                        </span>
                      )}
                    </div>
                    {m.target && <p style={{ fontSize: 10, color: '#bbbbbb' }}>Target {m.target}</p>}
                  </div>
                ))}
              </div>

              {/* Right column — Actions */}
              <div style={{ flex: '1 1 50%' }}>
                {block.actions.slice(0, 3).map((a, i) => (
                  <div key={i} className="flex items-start gap-2" style={{ marginBottom: 8 }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: dotColor, marginTop: 5, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: '#444444', lineHeight: 1.4, flex: 1 }}>
                      {a.text}
                      {a.soap && (
                        <button
                          onClick={() => toast.success('Added to SOAP', { description: a.text.slice(0, 50) })}
                          style={{ fontSize: 10, color: '#6b5ce7', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', marginLeft: 4 }}
                        >+SOAP</button>
                      )}
                    </span>
                  </div>
                ))}
                {block.actions.length > 3 && (
                  <p style={{ fontSize: 11, color: '#6b5ce7', cursor: 'pointer' }}>{block.actions.length - 3} more &rarr;</p>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between" style={{ borderTop: '0.5px solid #f5f5f5', padding: '7px 16px' }}>
            <span style={{ fontSize: 11, color: '#bbbbbb' }}>Last reviewed: {block.lastReviewed}</span>
            <button onClick={() => toast.info('Opening full history')}
              style={{ fontSize: 11, color: '#6b5ce7', cursor: 'pointer', background: 'none', border: 'none', fontWeight: 500 }}>
              View full history &rarr;
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ── Preventive Care Block ──
const PreventiveCareBlock = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  if (!data || !data.items) return null;

  const overdueCount = data.items.filter(i => i.status === 'overdue').length;

  return (
    <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, overflow: 'hidden' }}
      data-testid="preventive-care-block">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2"
        style={{ padding: '10px 16px', cursor: 'pointer', background: 'transparent' }}
        data-testid="preventive-toggle"
      >
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 500, color: '#111111' }}>Preventive care</span>
        <span style={{
          fontSize: 11, fontWeight: 500, borderRadius: 20, padding: '2px 8px',
          background: overdueCount > 0 ? '#fef2f2' : '#f0fdf4',
          color: overdueCount > 0 ? '#b91c1c' : '#16a34a',
        }}>{overdueCount > 0 ? `${overdueCount} overdue` : 'All current'}</span>
        <div className="flex-1" />
        {expanded
          ? <ChevronUp style={{ width: 11, height: 11, color: '#cccccc' }} />
          : <ChevronDown style={{ width: 11, height: 11, color: '#cccccc' }} />
        }
      </button>

      {expanded && (
        <div style={{ borderTop: '0.5px solid #f5f5f5' }}>
          {data.items.map((item, i) => {
            const Icon = item.status === 'current' ? CheckCircle2 : item.status === 'overdue' ? XCircle : Clock;
            const iconColor = item.status === 'current' ? '#22c55e' : item.status === 'overdue' ? '#ef4444' : '#6b7280';
            const dueDateColor = item.status === 'overdue' ? '#ef4444' : '#22c55e';
            const isLast = i === data.items.length - 1;

            return (
              <div key={i} className="flex items-center justify-between"
                style={{
                  padding: '8px 16px',
                  borderBottom: isLast ? 'none' : '0.5px solid #f5f5f5',
                }}
                data-testid={`preventive-item-${i}`}>
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <Icon style={{ width: 16, height: 16, color: iconColor, flexShrink: 0 }} />
                  <div className="flex-1 min-w-0">
                    <span style={{ fontSize: 12, color: '#333333' }}>{item.name}</span>
                    <span style={{ fontSize: 11, color: '#aaaaaa', marginLeft: 8 }}>{item.freq}</span>
                  </div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right">
                    <p style={{ fontSize: 11, color: '#aaaaaa' }}>Last: {item.lastDone}</p>
                    <p style={{ fontSize: 11, color: dueDateColor, fontWeight: item.status === 'overdue' ? 500 : 400 }}>
                      Due: {item.dueDate}
                    </p>
                  </div>
                  {item.status === 'overdue' && item.actionLabel && (
                    <button
                      onClick={() => toast.success(`${item.actionLabel}: ${item.name}`)}
                      style={{ fontSize: 11, color: '#6b5ce7', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {item.actionLabel} &rarr;
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Main Panel ──
const DiseaseBlocksPanel = ({ patientId, visitType }) => {
  const patientData = DISEASE_DATA[patientId];
  if (!patientData) return null;

  const getDefaultExpanded = (condition) => {
    if (!visitType) return false;
    const vt = visitType.toLowerCase();
    if (vt.includes('diabetes') && condition === 'diabetes') return true;
    if ((vt.includes('htn') || vt.includes('hypertension') || vt.includes('bp')) && condition === 'hypertension') return true;
    if (vt.includes('lipid') && condition === 'hyperlipidemia') return true;
    if (vt.includes('annual') || vt.includes('physical')) return false; // all collapse for annual
    return false;
  };

  const anyMatched = patientData.blocks.some(b => getDefaultExpanded(b.condition));

  return (
    <div data-testid="disease-blocks-panel">
      {/* Section label + severity color key */}
      <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
        <p style={{
          fontSize: 11, fontWeight: 500, color: '#aaaaaa',
          textTransform: 'uppercase', letterSpacing: '0.06em',
          padding: '0 2px',
        }}>Conditions</p>
        <div className="flex items-center" style={{ gap: 10 }} data-testid="severity-legend">
          {SEVERITY_LEGEND.map(item => (
            <div key={item.label} className="flex items-center" style={{ gap: 3 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: item.color }} />
              <span style={{ fontSize: 9, color: '#999999' }}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {patientData.blocks.map((block, i) => (
          <ConditionBlock
            key={block.condition}
            block={block}
            defaultExpanded={anyMatched ? getDefaultExpanded(block.condition) : i === 0}
          />
        ))}
        <PreventiveCareBlock data={patientData.preventive} />
      </div>
    </div>
  );
};

export default DiseaseBlocksPanel;
